import { defineComponent as _defineComponent } from 'vue'
import TTMassEntryTable2 from "@/components/modules/entry/TTMassEntryTable2.vue";
import TTEditRowsModal, {
  TTEditForm,
} from "@/components/modules/entry/TTEditRowsModal.vue";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import {
  Release,
  AppMode,
  User,
  StoredTtPage,
  WindowSettings,
} from "@/CodegenTypes";
import { TTEntryInput, TTDay } from "@/types";
import { parseTtEntryInputs } from "@/helpers/tt/parseTtEntryInputs";
import { clamp, compact } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import { calculateTtEntries } from "@/helpers/tt/calculateTtEntries";
import { formatTimeSetting } from "@/helpers/formatTimeSetting";
import { getTtValidDayOptions } from "@/helpers/getTtValidDayOptions";
import { useRootStore } from "@/store/pinia";
import { useTranslation } from "@/store/translation";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'TTFixedBet',
  setup(__props) {

const route = useRoute();
const rootStore = useRootStore();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const selectedPageId = ref<null | StoredTtPage["id"]>(null);
const releases = ref<null | Release[]>(null);
const selectedDayIndex = ref(0);
const page = ref("");
const showEditModal = ref(false);
const isSubmitting = ref(false);
const showAccountSelectionModal = ref(false);
const editForm = ref({
  rowsFrom: undefined,
  rowsTo: undefined,
  unit: undefined,
  mode: undefined,
} as TTEditForm);
const MAX_ROWS = 40;
const tables = [{ startingIndex: 0 }, { startingIndex: 20 }];
const entries = ref<TTEntryInput[]>([]);
let entriesInitialized = false;

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const releasesVariables = computed(() => {
  return {
    appMode: AppMode.Tt,
  };
});
const mountedReleasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
  };
});
const windowSettingsVariables = computed(() => {
  return {
    appMode: rootStore.appMode as AppMode,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const storedTtPagesVariables = computed(() => {
  return {
    storedPageType: "fixed",
    includeDownlines: true,
    userId: selectedAccountId.value,
  };
});
const { result: releasesResult, loading: releasesLoading } = useQuery<{
  releases: Release[];
}>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: windowSettingsResult, loading: windowSettingsLoading } = useQuery<{
  windowSettings: WindowSettings;
}>(queries.windowSettings, windowSettingsVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.userWithCreditSummary,
  userVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);
const {
  result: storedTtPagesResult,
  loading: storedTtPagesLoading,
  refetch: refetchStoredTtPages,
} = useQuery<{ user: { storedTtPages: StoredTtPage[] } }>(
  queries.userStoredTtPages,
  storedTtPagesVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

const user = computed(() => userResult.value?.user ?? null);
const selectedAccount = computed(() => user.value);
const windowSettings = computed(() => windowSettingsResult.value?.windowSettings ?? null);
const storedTtPages = computed(() => storedTtPagesResult.value?.user?.storedTtPages ?? null);
const apolloLoading = computed(
  () =>
    releasesLoading.value ||
    windowSettingsLoading.value ||
    userLoading.value ||
    storedTtPagesLoading.value
);
const creditForDisplay = computed(() => {
  return {
    creditGiven: selectedAccount.value?.creditGiven || 0,
    creditUsed: selectedAccount.value?.creditSummary.creditUsed || 0,
    creditBalance: selectedAccount.value?.creditSummary.creditBalance || 0,
  };
});
const selectedDay = computed<TTDay>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 1) as TTDay;
    }
    return 1;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const upcomingReleases = computed(() =>
  releases.value?.filter((release) => !release.isOver)
);
const validEntries = computed(() => compact(parseTtEntryInputs(entries.value)));
const calculations = computed(() => calculateTtEntries(compact(validEntries.value)));
const pageOptions = computed(() => {
  const defaultOption = { label: "-- New --", value: null };

  if (!storedTtPages.value) {
    return [defaultOption];
  }
  const allOptions = storedTtPages.value.map((storedPage) => {
    return {
      label: `[${storedPage.userId.toUpperCase()}, ${storedPage.username}] ${storedPage.name}`,
      value: storedPage.id,
    };
  });

  return [defaultOption, ...allOptions];
});
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getTtValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "4=<Thu>", value: 4, defaultPriority: false },
  ];

  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});
const selectedPage = computed(() => {
  if (!storedTtPages.value) {
    return null;
  }
  return storedTtPages.value.find((storedPage) => storedPage.id === selectedPageId.value);
});

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
      initialiseEntries();
    }
  },
  { immediate: true }
);
watch(selectedPage, fillWithPage);

onMounted(async () => {
  const result = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: mountedReleasesVariables.value,
  });
  releases.value = result.data.releases;
  initialiseEntries();
  const pageIdQuery = route.query.pageId;
  if (typeof pageIdQuery === "string") {
    selectedPageId.value = pageIdQuery;
  }
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

function initialiseEntries() {
  if (!releases.value || entriesInitialized) {
    return;
  }
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;
  doClearEntries();
  entriesInitialized = true;
}

function fillWithPage() {
  const currentPage = selectedPage.value;
  doClearEntries();
  if (!currentPage) {
    page.value = "";
    return;
  }
  page.value = currentPage.name;

  const newEntries = [...entries.value];
  for (const [index, entry] of currentPage.entries.entries()) {
    const numbers = entry.numbersText.split("-");
    newEntries[index].number1 = numbers[0];
    newEntries[index].number2 = numbers[1] || "";
    newEntries[index].number3 = numbers[2] || "";
    newEntries[index].number4 = numbers[3] || "";
    newEntries[index].number5 = numbers[4] || "";
    newEntries[index].number6 = numbers[5] || "";
    newEntries[index].number7 = numbers[6] || "";
    newEntries[index].number8 = numbers[7] || "";
    newEntries[index].number9 = numbers[8] || "";
    newEntries[index].number10 = numbers[9] || "";
    newEntries[index].unit = entry.unit;
    newEntries[index].mode = entry.mode || undefined;
  }
  entries.value = newEntries;
}

function toggleEditModal() {
  showEditModal.value = !showEditModal.value;
}

function focus(field: "number" | "big" | "small", inputTargetIndex: number) {
  let targetIndex = inputTargetIndex;
  if (targetIndex < 0) {
    targetIndex = 59;
  } else if (targetIndex > 59) {
    targetIndex = 0;
  }
  const tablesEles = vm.$refs["mass-entry-table"] as any;
  for (const [i, table] of tables.entries()) {
    const startingIndex = table.startingIndex;
    if (targetIndex >= startingIndex && targetIndex < startingIndex + 20) {
      tablesEles[i].focus(field, targetIndex);
    }
  }
  return;
}

function performEditRows(newEditForm: TTEditForm) {
  if (!newEditForm.rowsFrom || !newEditForm.rowsTo) {
    return;
  }

  const rowsFrom = clamp(newEditForm.rowsFrom, 0, MAX_ROWS);
  const rowsTo = clamp(newEditForm.rowsTo, 0, MAX_ROWS);
  const range = [rowsFrom, rowsTo].sort((a, b) => a - b);

  for (let i = range[0] - 1; i <= range[1] - 1; i++) {
    entries.value[i].unit = newEditForm.unit;
    entries.value[i].mode = newEditForm.mode;
  }
  editForm.value = {
    rowsFrom: undefined,
    rowsTo: undefined,
    unit: undefined,
    mode: undefined,
  };
  showEditModal.value = false;
}

async function clearEntries(event: Event) {
  vm.$confirm.require({
    target: event.currentTarget,
    group: "popups",
    header: "Clear the form?",
    message: "All existing inputs will be deleted.",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      doClearEntries();
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

function doClearEntries() {
  const newEntries = [];

  for (let i = 0; i < MAX_ROWS; i++) {
    newEntries.push({
      day: selectedDay.value,
      number1: "",
      number2: "",
      number3: "",
      number4: "",
      number5: "",
      number6: "",
      number7: "",
      number8: "",
      number9: "",
      number10: "",
      unit: undefined,
      mode: undefined,
    });
  }
  entries.value = newEntries;
}

async function handleDelete() {
  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  vm.$confirm.require({
    header: "Confirm?",
    message: t("Are you sure to delete the saved bet?"),
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        isSubmitting.value = true;
        await apolloClient.mutate({
          mutation: mutations.deleteSavedTtPage,
          variables: {
            id: selectedPage.value?.id,
          },
        });
        await refetchStoredTtPages(storedTtPagesVariables.value);

        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Saved bet deleted.",
          life: 3000,
        });
      } catch (err) {
        errorMutationNotification(err);
      } finally {
        isSubmitting.value = false;
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

async function handleSave() {
  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  const parsedEntries = validEntries.value;

  if (!parsedEntries.length) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "Please enter number details for entries",
      life: 3000,
    });
    return;
  }

  vm.$confirm.require({
    header: "Confirm?",
    message: t("Are you sure to save the entries for SGTOTO betting?"),
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        isSubmitting.value = true;
        const { data } = await apolloClient.mutate({
          mutation: mutations.saveTtEntry,
          variables: {
            page: {
              id: selectedPage.value?.id || undefined,
              page: page.value,
              entries: parsedEntries,
              userId: selectedAccountId.value,
              storedPageType: "fixed",
            },
          },
        });
        selectedPageId.value = data.saveTtEntry;
        await refetchStoredTtPages(storedTtPagesVariables.value);
        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Entry saved successfully.",
          life: 3000,
        });
        fillWithPage();
      } catch (err) {
        errorMutationNotification(err);
      } finally {
        isSubmitting.value = false;
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

return { __sfc: true,route, rootStore, vm, $t, t, selectedPageId, releases, selectedDayIndex, page, showEditModal, isSubmitting, showAccountSelectionModal, editForm, MAX_ROWS, tables, entries, entriesInitialized, selectedAccountId, releasesVariables, mountedReleasesVariables, windowSettingsVariables, userVariables, storedTtPagesVariables, releasesResult, releasesLoading, windowSettingsResult, windowSettingsLoading, userResult, userLoading, storedTtPagesResult, storedTtPagesLoading, refetchStoredTtPages, user, selectedAccount, windowSettings, storedTtPages, apolloLoading, creditForDisplay, selectedDay, upcomingReleases, validEntries, calculations, pageOptions, validDayOptions, dayOptions, selectedPage, getRouteQueryValue, initialiseEntries, fillWithPage, toggleEditModal, focus, performEditRows, clearEntries, doClearEntries, handleDelete, handleSave, TTMassEntryTable2, TTEditRowsModal, AccountSelectModal, formatDollar, formatTimeSetting }
}

})